import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { createClient } from "../api/clients";
import { ApiClientError } from "../types/api";
import type { Client } from "../types/client";
import { BUSINESS_INDUSTRY_GROUPS, PERSONAL_INDUSTRY } from "../data/industryGroups";
import { ENTITY_TYPES } from "../data/entityTypes";
import { ErrorMessage } from "./ErrorMessage";
import { AnimatedButton } from "./ui/AnimatedButton";
import { SearchableSelect } from "./ui/SearchableSelect";

interface CreateClientFormProps {
  onCreated: (client: Client) => void;
  /** The entity type chosen when the form opens: "personal" from the "Add personal books" door. */
  initialType?: string;
}

export function CreateClientForm({
  onCreated,
  initialType = "general",
}: CreateClientFormProps): ReactNode {
  const [name, setName] = useState("");
  const [type, setType] = useState(initialType);
  const [industry, setIndustry] = useState("general");
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [loading, setLoading] = useState(false);
  const isPersonal = type === "personal";

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim()) {
      setError("Business name is required");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const client = await createClient(
        name.trim(),
        type,
        isPersonal ? PERSONAL_INDUSTRY : industry,
      );
      onCreated(client);
      setName("");
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "CLIENT_LIMIT_EXCEEDED") {
        setLimitReached(true);
      }
      setError(err instanceof Error ? err.message : "Failed to create business");
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.form
      onSubmit={(e) => void handleSubmit(e)}
      className="create-client-form"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
    >
      <h3>{isPersonal ? "Add personal books" : "Create a new business"}</h3>
      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => {
            setError("");
            setLimitReached(false);
          }}
        />
      )}
      {limitReached && (
        <p className="upgrade-prompt">
          <Link to="/upgrade">About the limit on businesses and personal books.</Link>
        </p>
      )}

      <label htmlFor="new-client-name">
        {isPersonal ? "A name for these books" : "Business name"}
      </label>
      <input
        id="new-client-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={isPersonal ? "Name for your personal books" : "Enter business name"}
        disabled={loading}
      />

      {!isPersonal && (
        <>
          <label htmlFor="new-client-industry">Industry</label>
          <SearchableSelect
            id="new-client-industry"
            groups={BUSINESS_INDUSTRY_GROUPS}
            value={industry}
            onChange={setIndustry}
            disabled={loading}
            placeholder="Search industries..."
          />
        </>
      )}

      <label htmlFor="new-client-type">Entity type</label>
      <select
        id="new-client-type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        disabled={loading}
      >
        {ENTITY_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>

      <AnimatedButton type="submit" variant="primary" disabled={loading}>
        {loading ? "Creating..." : isPersonal ? "Add personal books" : "Create business"}
      </AnimatedButton>
    </motion.form>
  );
}
